import React, { memo, useRef, useEffect, useCallback, useState} from 'react';
import {getCoordinates} from "effects/midi/mappable/getCoordinates.js";
import PlaneMacro from './PlaneMacro.js';
import DrawnPath from './DrawnPath.js';
import {useDropPreset} from './useDropPreset.js';
import {useActions} from "components/effects/plane/useActions.js";
import PlaneToolbar from "components/effects/plane/PlaneToolbar.js";
import {PLANAR_SCRIPT} from 'constants/script-types.js';
import UserScriptComponent from "components/effects/scripting/UserScriptComponent.js";
import {doc} from 'effects/midi/mappable/lang/documentation.js';
import {useDensities} from './useDensities.js';
import {dist} from './utils.js';
import ConditionedComponent from "components/ConditionedComponent.js";
import PlaneSnapshots from './PlaneSnapshots.js';
import {useKey} from './useKey.js';
import RegionMode from './RegionMode.js';
import PlaneAlgo from './PlaneAlgo.js';
import PlaneGroup from './PlaneGroup.js';
import {useWheel} from './useWheel.js';
import PlaneSidebar from './PlaneSidebar.js';
import {setEffectParameterValues} from 'actions/index.js';
import {setParameterMinMax} from 'actions/index.js';
import Colors from 'components/Colors.js';
import {useMouse} from './useMouse.js';
import {connect} from 'react-redux';
import {WIDTH, HEIGHT, PADDING} from './constants.js';
import '../../../Plane.scss';

const colors = new Colors();


const Plane = ({
    setParameterMinMax,
    effect,
    parameterSelected,
    setEffectParameterValues,
    sequencerEngine,
    macros=[],
    ... props
}) => { 
      

    let ref = useRef(undefined);
    let [isRegionMode, setIsRegionMode] = useState(false);
    let [filteredParameters, setFilteredParameters] = useState(undefined);
    let [expanded, setExpanded] = useState(effect.scriptExpanded);
    let [macroToShow, setMacroToShow] = useState(undefined);


    let {
        isDensitiesMode,
        setIsDensitiesMode,
        onClickDensity,
        setSelectedDensity,
        selectedDensity,
        densities,
        setDensities} = useDensities(effect);

    let {
        setDrawMode,
        drawMode,
        setSelected,
        selection,
        startDragging,
        onMouseDown,
        onMouseMove,
        onMouseUp,
        selected} = useMouse({
        ref,
        setSelectedDensity,
        setDensities,
        effect,
        setEffectParameterValues,
        filteredParameters,
        isRegionMode,
            isDensitiesMode,
            macroToShow
    });

    useEffect(() => {
        if (selected && !filteredParameters) {
            if (selected.length == 0) {
                setMacroToShow(undefined);
            } else if (macroToShow && selected[0].isPlaneMacro && selected[0] !== macroToShow) {
                setMacroToShow(selected[0]);
            }
        }
    }, [selected, setMacroToShow, filteredParameters, macroToShow]);

    useEffect(() => {
        if (!filteredParameters) {
            return;
        }
        let p = filteredParameters.find(x => x.planeMacro);
        if (p) {
            setMacroToShow(p.planeMacro);
        }
    }, [filteredParameters, setMacroToShow]);

    const {mergeGroups, lock, locked, grouped} = useActions({
        effect, selected, setSelected, parameterSelected, setIsRegionMode});

    const {onDropPreset} = useDropPreset(effect);
    const {unmap, group} = useKey({
        selectedDensity,
        setDensities,
        effect,
        setSelected,
        selected,
        filteredParameters,
        setFilteredParameters,
        sequencerEngine,
        expanded,
        setEffectParameterValues,
        setParameterMinMax,
        isRegionMode});

    useWheel({
        ref,
        effect,
        setDensities,
        selectedDensity,
        setEffectParameterValues,
        selected,
        setSelected,
        isRegionMode});

    const height = selection && Math.abs(selection.y2 - selection.y1);
    const width = selection && Math.abs(selection.x2 - selection.x1);
    let parameters = (filteredParameters || effect._parameters).filter(
        s => (macroToShow && macroToShow === s.planeMacro) || !s.planeMacro);

    let coords = {};
    getCoordinates(parameters)
        .forEach((c, i) => coords[parameters[i].id] =c);

    // if there are 2 or more groups (non-grouped counts as a group here),
    // then we allow the user to merge those together

    const sidebar = (
        <PlaneSidebar
          macroToShow={macroToShow}
          setMacroToShow={setMacroToShow}
          isBoundsMode={isRegionMode}
          setSelected={setSelected}
          expanded={expanded}
          filteredParameters={filteredParameters}
          setFilteredParameters={setFilteredParameters}
          selected={selected}
          effect={effect}
          setSelected={setSelected}/>);
    
    let className = "plane-inner";
    if (expanded) {
        className += " expanded";
    } 

    return (
        <div
          onDrop={onDropPreset}
          className={className}>
        <div className="vertical-pane">
          <div className="plane-container">
            <div
              draggable={true}
              onDragStart={e => e.preventDefault()}
              onDragOver={(e) => e.preventDefault()}
              onMouseDown={(e) => e.stopPropagation()}
              className="plane-view">
              <div className="y-axis">
                Modulation
              </div>
              <div className="x-axis">
                Parameter Value
              </div>
              <svg
                className="drawn-svg" height={height} width={WIDTH}>
                {!window.isNFT && <DrawnPath drawnPath={effect.currentPath} effect={effect}/>}
              </svg>
              <svg
                ref={ref}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}
                width={WIDTH} height={HEIGHT}>
                {densities.map(
                    (density, i) =>
                        <circle
                          key={i}
                          opacity={density.viscosity / 10.0}
                          className={selectedDensity === density ? "selected density-region" : "density-region"}
                          onMouseDown={(e) => onClickDensity(e, density)}
                          cx={density.x}
                          cy={density.y}
                          r={density.radius}/>)}
                {isRegionMode ?
                 <RegionMode width={WIDTH} height={HEIGHT} selected={selected} effect={effect}/>
                 : ''}
                {
                 isDensitiesMode ?
                 <circle cx={Math.min(selection.x1, selection.x2)} cy={Math.min(selection.y1, selection.y2)} r={dist(selection)} fill="red"/> : selection && !isNaN(selection.x2-selection.x1) && !isNaN(selection.y2-selection.y1) ?
                 <rect
                   x={selection.x1}
                   y={selection.y1}
                   width={Math.abs(selection.x2 - selection.x1)}
                   height={Math.abs(selection.y2 - selection.y1)}
                   fill="#b00909"
                 />
                 :
                 []}
                {!isDensitiesMode && !filteredParameters ?
                 effect.groups.map(
                     group => <PlaneGroup key={group.id} group={group}/>) : ''}
                {!isDensitiesMode ? parameters
                 .filter(x => !selected.includes(x))
                 .filter(x => !x.isPlaneMacro && (macroToShow === x.planeMacro || !x.planeMacro))
                 .filter(x => coords[x.id])
                 .map(
                     param =>
                         <Star
                           key={param.id}
                           x={coords[param.id].x}
                          bpm={props.bpm}
                           y={coords[param.id].y}
                           startDragging={startDragging}
                           isSelected={false}
                           parameter={param}/>) : []}
                {!isDensitiesMode ?
                 selected.filter(x => !x.isPlaneMacro && (macroToShow === x.planeMacro || !x.planeMacro))
                 .filter(x => coords[x.id])
                 .map(
                    param =>
                        <Star
                          key={param.id}
                          bpm={props.bpm}
                          x={coords[param.id].x}
                          y={coords[param.id].y}
                          startDragging={startDragging}
                          isSelected={true}
                          parameter={param}/>) : []}
                {macros.map(
                    macro => {
                        let {x,y,diameter} = macro.getGeometry();
                        
                        return (<PlaneMacro
                                  key={macro.id}
                                  isShowing={macroToShow === macro}
                                  isSelected={selected.includes(macro)}
                                  bpm={props.bpm}
                                  startDragging={startDragging}
                                  coordinates={{x,y}}
                                  diameter={diameter}
                                  macro={macro}/>);
                    })}
              </svg>
            </div>
            {!expanded ? sidebar : ''}
         </div>
          <PlaneToolbar
            setSelected={setSelected}
            isDraggable={props.isDraggable}
            drawMode={drawMode}
            setDrawMode={setDrawMode}
            isRegionMode={isRegionMode}
            effect={effect}
            setExpanded={setExpanded}
            expanded={expanded}
            setIsRegionMode={setIsRegionMode}
            unmap={unmap}
            selected={selected}
            mergeGroups={mergeGroups}
            group={group}
            grouped={grouped}
          >
            {props.children}
          </PlaneToolbar>
       </div>
          {expanded ? sidebar : ''}
          {expanded ?
           <UserScriptComponent
             isDraggable={props.isDraggable}
             scriptType={PLANAR_SCRIPT}
             expanded={true}
             setExpanded={() => {
                 effect.scriptExpanded = undefined;
                 setExpanded(undefined);
             }}
             doc={doc}
             sequencerEngine={sequencerEngine}
             effect={expanded}/> : ''}
        </div>
    );
};

const Star = memo(({bpm, parameter, isSelected, startDragging, x, y}) => {
    let fill = colors.getColorForHash(parameter.name);
    let stroke = fill;
    if (isSelected) {
        fill = "black";
    }
    let blur = (bpm / 85.0);
    let style = {
    };
    style.filter= "blur(" + blur + 'px)';
    if (isSelected) {
        return (
            <circle
              style={style}
              onMouseDown={(e) => startDragging(e, parameter)}
              opacity={parameter.locked2D ? 0.3 : 1}
              className={isSelected ? "star selected" : "selected"}
              cx={Math.round(x)} cy={Math.round(y)} r={7} fill={fill} stroke={stroke}/>
        );
    } else {
        return (
            <circle
              style={style}
              opacity={parameter.locked2D ? 0.3 : 1}
              onMouseDown={(e) => startDragging(e, parameter)}
              className={isSelected ? "star selected" : "selected"}
              cx={Math.round(x)} cy={Math.round(y)} r={7} fill={fill}/>
        );
    }
});

const mapStateToProps = (state, props) => {
    let x ={
    };
    props.effect._parameters.map(
        p => x[p.id] = state.effects.effectParameters[p.id]);
    props.effect._parameters.map(
        p => x[p.id + 'depth'] = p.depth);
    return {
        ...x,
        macros: state.plane.macros[props.effect.id],
        parameterSelected: state.effects.accumulatorParameterSelected,
        bpm: props.effect.track.sequencerEngine.bpm
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setEffectParameterValues: x => dispatch(setEffectParameterValues(x)),
        setParameterMinMax: x => dispatch(setParameterMinMax(x))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Plane);
